import { Spinner } from 'components/shared/loader/Spinner';
import {
  ACCESS_VIEWS,
  ACCOUNT_TYPE,
  areas,
  BANNER_LEGENDS,
  CHART_VIEWS,
  NOT_AVAILABLE,
  NULL_STRING,
  NUMBER_ZERO,
} from 'constants/constants';
import { Fade } from 'react-awesome-reveal';
import { usePromiseTracker } from 'react-promise-tracker';
import classes from '../Banner.module.scss';
import PieChart from './PieChart';
import { QLIK_DATATYPE } from 'constants/constants';
import infoIcon from 'assets/images/svg/tooltip_icon_info.svg';
import { upperFirst } from 'lodash';
import { memo } from 'react';

const Loader = memo(({ noData, isLoading }) => {
  return (
    <Fade className={classes['loader-wrapper']}>
      {!isLoading && noData && <p className='text-center'>{NULL_STRING}</p>}
      <Spinner area={areas.bannerSegment} />
    </Fade>
  );
});

const ChartHeader = memo(({ title, subTitle }) => (
  <Fade className={classes['chart-section-fade']}>
    <div className='title__small text-center mb-2'>
      <div className='title__bold '>{title}</div>
      <div>{subTitle}</div>
    </div>
  </Fade>
));

const ChartFooter = memo(({ children }) => (
  <div className={`${classes['pie-chart-footer']} title title__xx-small`}>
    {children}
  </div>
));

const PieChartSection = ({
  accessView,
  view,
  selectChartView,
  payerPieDate,
  showPayerChart,
  payerPieData,
  beamChartSiteQuarter,
  beamQuarter,
  showSiteChart,
  showChart,
  beamChartSite,
  beamChart,
  contract,
  id340BValue,
  onsegmentChange,
  contractMembership,
}) => {
  const { promiseInProgress } = usePromiseTracker({
    area: areas.bannerSegment,
  });

  const legend = {
    orient: 'vertical',
    align: 'auto',
    icon: 'circle',
    itemWidth: 9,
    bottom: 0,
    left: 0,
    height: '45%',
    width: '100%',
    textStyle: {
      color: '#ffffff',
      fontSize: '9px',
    },
    formatter: function (name) {
      return `${name}`;
    },
  };

  const contractLegend = {
    ...legend,
    triggerEvent: true,
    textStyle: {
      ...legend.textStyle,
      rich: {
        label: {
          color: '#ffffff',
          fontSize: '9px',
          lineHeight: 12,
        },
        info: {
          width: 10,
          height: 10,
          lineHeight: 12,
          backgroundColor: {
            image: infoIcon,
          },
          verticalAlign: 'top',
        },
        gap: {
          width: 6,
          height: 10,
          lineHeight: 12,
        },
      },
    },
    formatter: function (name) {
      if (
        name === BANNER_LEGENDS.NON_CONTRACTED ||
        (name === BANNER_LEGENDS.B_340 && contractMembership)
      ) {
        return `{label|${name}}{gap| }{info| }`;
      }
      return `${name}`;
    },
    tooltip: {
      show: true,
      confine: true,
      backgroundColor: '#ffffff',
      // Position tooltip near the cursor (info icon) with a slight offset
      position: function (point, params, dom, rect, size) {
        if (contract?.length > 6) {
          return [rect.x, rect.y + 20];
        } else {
          return [rect.x, rect.y - 30];
        }
      },
      padding: [5, 10, 5, 10],
      extraCssText:
        'white-space:pre-line; text-wrap:wrap; box-shadow: 0px 0px 4px #0000003D; overflow: visible;',
      formatter: function (params) {
        const name = params?.name ?? params;
        let content = '';
        if (name === BANNER_LEGENDS.B_340 && contractMembership) {
          content = `${name} (${contractMembership})`;
        } else if (name === BANNER_LEGENDS.NON_CONTRACTED) {
          content = 'Non-contracted Sales are estimated.';
        } else {
          return '';
        }
        return '<div style="position:relative;">' + content + '</div>';
      },
      textStyle: {
        fontSize: 10,
        color: '#000000',
      },
    },
  };

  const renderContractSegmentChart = () => (
    <div>
      <ChartHeader
        title='Contract Segment'
        subTitle={
          view === ACCOUNT_TYPE.site ? beamChartSiteQuarter : beamQuarter
        }
      />
      <>
        <div className={classes['chart-container']}>
          {!promiseInProgress && (showSiteChart || showChart) && (
            <PieChart
              chartData={
                view === ACCOUNT_TYPE.site
                  ? beamChartSite?.contractSegment
                  : beamChart?.contractSegment
              }
              chartType={QLIK_DATATYPE.contractPie}
              legend={contractLegend}
              chartAllData={beamChartSite}
              screenType={view}
            />
          )}
          <Loader
            noData={!showSiteChart && !showChart}
            isLoading={promiseInProgress}
          />
        </div>
        <ChartFooter>
          <>
            <span className='pie-chart-footer-title'>340B ID :</span>
            <span className='ms-1'>
              {id340BValue?.length > NUMBER_ZERO ? id340BValue : NOT_AVAILABLE}
            </span>
          </>
        </ChartFooter>
      </>
    </div>
  );

  const renderPayerMixChart = () => (
    <div>
      <ChartHeader
        title={'Payer Mix'}
        subTitle={payerPieDate}
      />
      <>
        <div className={classes['chart-container']}>
          {!promiseInProgress && showPayerChart && (
            <PieChart
              chartData={payerPieData}
              chartType={QLIK_DATATYPE.payerPie}
              legend={legend}
            />
          )}
          <Loader
            noData={!showPayerChart}
            isLoading={promiseInProgress}
          />
        </div>
        {selectChartView === CHART_VIEWS.PAYER && (
          <ChartFooter>
            <>
              Estimated Parent Payer Contribution to{' '}
              {view === ACCOUNT_TYPE.account
                ? upperFirst(ACCOUNT_TYPE.account)
                : upperFirst(ACCOUNT_TYPE.site)}
            </>
          </ChartFooter>
        )}
      </>
    </div>
  );

  const renderAllAccessView = () => (
    <>
      <div className={`${classes['header_allign']} d-flex flex-row`}>
        <div
          className={`title__x-small text-center ${classes['header_style']} ${selectChartView === CHART_VIEWS.CONTRACT ? classes['activeHeader'] : classes['header']}`}
          onClick={() => onsegmentChange(CHART_VIEWS.CONTRACT)}>
          <div className='title__bold'>Contract Segment</div>
          <div>
            {view === ACCOUNT_TYPE.site ? beamChartSiteQuarter : beamQuarter}
          </div>
        </div>
        <div
          className={` title__x-small text-center ${classes['header_style']} ${selectChartView === CHART_VIEWS.PAYER ? classes['activeHeader'] : classes['header']}`}
          onClick={() => onsegmentChange(CHART_VIEWS.PAYER)}>
          <div className='title__bold'>Payer Mix</div>
          <div>{payerPieDate}</div>
        </div>
      </div>
      <div>
        {selectChartView === CHART_VIEWS.CONTRACT ? (
          <>
            <div className={classes['chart-container']}>
              {!promiseInProgress && (showSiteChart || showChart) && (
                <PieChart
                  chartData={
                    view === ACCOUNT_TYPE.site
                      ? beamChartSite?.contractSegment
                      : beamChart?.contractSegment
                  }
                  chartType={QLIK_DATATYPE.contractPie}
                  legend={contractLegend}
                  chartAllData={beamChartSite}
                  screenType={view}
                />
              )}
              <Loader
                noData={!showSiteChart && !showChart}
                isLoading={promiseInProgress}
              />
            </div>
            <ChartFooter>
              <>
                <span className={classes['pie-chart-footer-title']}>
                  340B ID :
                </span>
                <span className='ms-1'>
                  {id340BValue?.length > NUMBER_ZERO
                    ? id340BValue
                    : NOT_AVAILABLE}
                </span>
              </>
            </ChartFooter>
          </>
        ) : (
          <>
            <div className={classes['chart-container']}>
              {!promiseInProgress && showPayerChart && (
                <PieChart
                  chartData={payerPieData}
                  chartType={QLIK_DATATYPE.payerPie}
                  legend={legend}
                />
              )}
              <Loader
                noData={!showPayerChart}
                isLoading={promiseInProgress}
              />
            </div>
            {selectChartView === CHART_VIEWS.PAYER && (
              <ChartFooter>
                <>
                  Estimated Parent Payer Contribution to{' '}
                  {view === ACCOUNT_TYPE.account
                    ? upperFirst(ACCOUNT_TYPE.account)
                    : upperFirst(ACCOUNT_TYPE.site)}
                </>
              </ChartFooter>
            )}
          </>
        )}
      </div>
    </>
  );

  return (
    <div>
      {accessView === ACCESS_VIEWS.PAYER && renderPayerMixChart()}
      {accessView === ACCESS_VIEWS.CONTRACT && renderContractSegmentChart()}
      {accessView === ACCESS_VIEWS.ALL && renderAllAccessView()}
      {accessView === ACCESS_VIEWS.NONE && (
        <Fade>
          <p className='text-center'>{}</p>
        </Fade>
      )}
    </div>
  );
};

export default PieChartSection;
